<template lang="html">
  <v-content>
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />
    <v-dialog
      width="500"
      v-model="deleteConfirmationDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="deleteConfirmationDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Are you sure you want to delete <b>{{ documentToDelete.name }}</b
            >?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="deleteDocument(documentToDelete)"
          >
            Yes
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="deleteConfirmationDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="600"
      v-model="moveItemDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveItemDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title"> Specify new location </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">{{ deleteOldPath ? 'Move' : 'Copy' }} to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              label="Different entity"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              placeholder="Select new entity"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath) ? 'No folders' : 'Choose folder'
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath) || loading"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveItem()"
            :disabled="
              pathsInOrder.join('/') === currentPath && !newEntityLocation
            "
          >
            {{ deleteOldPath ? 'Move' : 'Copy' }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveItemDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="600"
      v-model="moveMultipleDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveMultipleDocumentsDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title"> Specify new location </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">{{ deleteOldPath ? 'Move' : 'Copy' }} to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              label="Different entity"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              placeholder="Select new entity"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath) ? 'No folders' : 'Choose folder'
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath)"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveMultipleDocuments()"
            :disabled="pathsInOrder.join('/') === currentPath"
          >
            {{ deleteOldPath ? 'Move' : 'Copy' }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveMultipleDocumentsDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="500"
      v-model="deleteMultipleConfirmationDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="deleteMultipleConfirmationDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Are you sure you want to delete
            {{ selectedDocuments.length }} items?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="deleteMultipleDocuments()"
          >
            Yes
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="deleteMultipleConfirmationDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="500"
      v-model="downloadMultipleConfirmationDialogOpened"
      @keydown.esc="downloadMultipleConfirmationDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title pb-0"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="info" :value="true" outline>
            Are you sure you want to download
            {{ selectedDocuments.filter(doc => doc.type === 'file').length }}
            files? (You cannot download folders) You might have to enable pop
            up's on the top if multiple files are not downloading.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="downloadMultipleDocuments()"
          >
            Yes
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="downloadMultipleConfirmationDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      max-width="1000px"
      v-model="previewDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedItem">
        <v-card-title class="dialog-title">
          <v-layout v-if="renameFile" align-center>
            <span class="caption">New Name:</span>
            <v-text-field
              v-model="newName"
              hide-details
              solo
              class="default-input mx-2"
            />
            <span class="subheading">.{{ documentExtension }}</span>
            <v-tooltip bottom class="mx-2">
              <i
                class="material-icons-outlined success--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameItem()"
                slot="activator"
              >
                check
              </i>
              <span>Save</span>
            </v-tooltip>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined error--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameFile = false"
                slot="activator"
              >
                cancel
              </i>
              <span>{{ $t('cancel') }}</span>
            </v-tooltip>
          </v-layout>
          <v-layout v-else>
            <span>{{ selectedItem.name }}</span>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 20px; cursor: pointer"
                @click="startRenaming()"
                slot="activator"
              >
                edit
              </i>
              <span>Rename</span>
            </v-tooltip>
          </v-layout>

          <v-spacer />
          <v-tooltip bottom class="mr-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="openMoveDialog(selectedItem)"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                drive_file_move
              </i>
            </v-btn>
            <span>Move document</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="openCopyDialog(selectedItem)"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                content_copy
              </i>
            </v-btn>
            <span>Copy document</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="
                downloadDocumentByLink(documentPreviewData, selectedItem.name)
              "
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                get_app
              </i>
            </v-btn>
            <span>Download to Computer</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="previewLoaded && documentPreviewData">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      width="600"
      v-model="importDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="importDocumentsDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="dialog-title pb-0">
          {{ $t('documents.uploadDocuments') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            {{ $t('alerts.dragAndDropFiles') }}
          </v-alert>
          <v-layout column>
            <v-flex class="text-xs-left mb-2 font-weight-bold">
              <v-layout align-center>
                <div class="py-3">{{ $t('documents.selectedDocuments') }}:</div>
                <v-spacer />
                <v-progress-circular
                  v-if="documentImporting"
                  indeterminate
                  size="25"
                  class="primary--text mr-2"
                />
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  :disabled="documentImporting"
                  @click="pickDocuments"
                >
                  <v-layout align-center>
                    <v-icon class="mr-2" size="20">add</v-icon>
                    {{ $t('documents.selectFiles') }}
                  </v-layout>
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
              No documents selected.
            </v-flex>
            <v-flex class="mb-4" v-else>
              <v-layout
                align-center
                :key="i"
                v-for="(doc, i) in documentsToImport"
              >
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ doc.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentsToImport.splice(i, 1)"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="importDocuments()"
            :disabled="documentImporting || isEmpty(documentsToImport)"
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              importDocumentsDialogOpened = false;
              documentsToImport = [];
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      width="600"
      v-model="createFolderDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="createFolderDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="dialog-title pb-0"> Create Folder </v-card-title>
        <v-alert
          v-if="isAdvisor"
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-4 mx-2"
        >
          Do not include slashes (/) or periods (.) and add <b>STAFF ONLY</b> in
          the folder name if you want the folder to be hidden from the client's
          view.
        </v-alert>
        <v-alert
          v-else
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-4 mx-2"
        >
          Do not include slashes (/) or periods (.).
        </v-alert>
        <v-card-text>
          <v-layout class="text-xs-left" column>
            <v-flex>
              <v-flex class="mb-2">Folder Name</v-flex>
              <v-text-field
                v-model="newFolderName"
                solo
                class="default-input"
                placeholder="e.g. Tax Returns"
                hide-details
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="createFolder"
            :disabled="
              !newFolderName.length ||
                newFolderName.includes('/') ||
                newFolderName.includes('.')
            "
          >
            Create
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="createFolderDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container
      style="max-width: 1000px"
      @drop.prevent="addFile"
      @dragover.prevent
    >
      <v-layout class="mt-3 mb-3" align-center>
        <h1 class="documents-title">Documents</h1>
      </v-layout>
      <v-layout column>
        <v-layout
          class="mt-2 mb-3 drag-and-drop"
          column
          align-center
          justify-center
        >
          <div><v-icon>cloud_upload</v-icon></div>
          <div>
            <span @click="openImportDocumentsDialog()"
              >Drop files here or
              <span class="clickable primary--text">click to select</span></span
            >
          </div>
        </v-layout>

        <v-layout class="mt-3 mb-2" align-center justify-center>
          <v-flex class="hidden-sm-and-down">
            <v-text-field
              v-model="documentSearch"
              append-icon="search"
              label="Search"
              single-line
              placeholder="Search"
              hide-details
              solo
              class="default-input search-input"
            />
          </v-flex>
          <v-spacer />
          <v-btn
            class="new-folder-btn"
            color="primary"
            @click="createFolderDialogOpened = true"
          >
            <v-icon left>add</v-icon>
            New folder
          </v-btn>
          <v-menu transition="slide-y-transition" bottom right class="my-0">
            <v-btn
              outline
              color="primary"
              slot="activator"
              class="action-btn"
              :disabled="isEmpty(selectedDocuments)"
            >
              <v-icon left>more_horiz</v-icon>
              Action
            </v-btn>
            <v-list class="caption">
              <v-list-tile @click="openMoveMultipleDocumentsDialog(true)">
                <v-list-tile-title>
                  Move Selected ({{ selectedDocuments.length }})
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="openMoveMultipleDocumentsDialog(false)">
                <v-list-tile-title>
                  Copy Selected ({{ selectedDocuments.length }})
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                @click="deleteMultipleConfirmationDialogOpened = true"
              >
                <v-list-tile-title>
                  Delete Selected ({{ selectedDocuments.length }})
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                @click="downloadMultipleConfirmationDialogOpened = true"
                v-if="
                  !isEmpty(selectedDocuments.filter(doc => doc.type === 'file'))
                "
              >
                <v-list-tile-title>
                  Download Selected ({{
                    selectedDocuments.filter(doc => doc.type === 'file').length
                  }})
                </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
        </v-layout>
        <v-layout class="subheading mt-3 mb-2" align-center justify-center>
          <v-layout align-center>
            <v-breadcrumbs :items="paths" class="breadcrumbs">
              <template v-slot:item="props">
                <span v-if="props.item.disabled">
                  {{ isEmpty(props.item.text) ? 'Home' : props.item.text }}
                </span>
                <span
                  class="clickable"
                  v-else
                  @click="goToPath(props.item.path)"
                >
                  {{ isEmpty(props.item.text) ? 'Home' : props.item.text }}
                </span>
              </template>
            </v-breadcrumbs>
          </v-layout>
        </v-layout>
        <v-layout column class="pb-4">
          <v-data-table
            :headers="
              $vuetify.breakpoint.xsOnly
                ? mobileDocumentHeaders
                : documentHeaders
            "
            :items="documents"
            hide-actions
            disable-initial-sort
            :loading="loading"
            :search="documentSearch"
            class="documents-table"
            :select-all="$vuetify.breakpoint.mdAndUp"
            v-model="selectedDocuments"
            item-key="name"
          >
            <template slot="items" slot-scope="props">
              <tr :key="props.index" v-if="!hiddenItem(props.item)">
                <td class="hidden-sm-and-down" style="max-width: 10px">
                  <v-checkbox
                    v-model="props.selected"
                    hide-details
                  ></v-checkbox>
                </td>
                <td class="text-xs-left">
                  <v-layout align-center>
                    <i
                      class="material-icons-outlined documents__icon folder-icon accent--text"
                      v-if="props.item.type === 'folder'"
                      >folder</i
                    >
                    <i
                      class="material-icons-outlined documents__icon file-icon"
                      v-else
                      >insert_drive_file</i
                    >
                    <v-layout v-if="props.item.renameFile" align-center>
                      <v-text-field
                        v-model="newName"
                        hide-details
                        solo
                        class="default-input mx-2"
                      />
                      <span
                        v-if="props.item.type === 'file'"
                        class="black--text subheading"
                        >.{{ documentExtension }}</span
                      >
                      <v-tooltip bottom class="mx-1 pt-1">
                        <i
                          class="material-icons-outlined success--text"
                          style="font-size: 20px; cursor: pointer"
                          @click="renameItem()"
                          slot="activator"
                        >
                          check
                        </i>
                        <span>Save</span>
                      </v-tooltip>
                      <v-tooltip bottom class="ml-1 pt-1">
                        <i
                          class="material-icons-outlined error--text"
                          style="font-size: 20px; cursor: pointer"
                          @click="props.item.renameFile = false"
                          slot="activator"
                        >
                          cancel
                        </i>
                        <span>{{ $t('cancel') }}</span>
                      </v-tooltip>
                    </v-layout>
                    <v-layout
                      v-else
                      class="ml-2"
                      style="cursor: pointer"
                      align-center
                      @click="
                        props.item.type === 'folder'
                          ? goToFolder(props.item.name)
                          : previewDocument(props.item)
                      "
                    >
                      <span class="ph-no-capture">{{ props.item.name }}</span>
                      <v-chip
                        class="mr-0"
                        small
                        v-if="props.item.name.includes('STAFF ONLY')"
                        >HIDDEN</v-chip
                      >
                    </v-layout>
                  </v-layout>
                </td>
                <td class="text-xs-left" v-if="$vuetify.breakpoint.smAndUp">
                  <v-layout align-center v-if="!!props.item.updated_at">
                    <span class="documents-row__updated-date">{{
                      moment(props.item.updated_at).format('lll')
                    }}</span>
                  </v-layout>
                  <v-layout align-center v-else>
                    <span class="documents-row__updated-date">-</span>
                  </v-layout>
                </td>
                <td>
                  <v-layout align-center>
                    <v-menu
                      transition="slide-y-transition"
                      bottom
                      right
                      class="my-0"
                    >
                      <i
                        slot="activator"
                        class="material-icons-outlined darkgrey--text"
                        :disabled="renameFile || loading"
                      >
                        more_vert
                      </i>
                      <v-list class="caption">
                        <v-list-tile @click="startRenaming(props.item)">
                          <v-list-tile-title> Rename </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile @click="openMoveDialog(props.item)">
                          <v-list-tile-title> Move </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile @click="openCopyDialog(props.item)">
                          <v-list-tile-title> Copy </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="openDeleteConfirmationDialog(props.item)"
                        >
                          <v-list-tile-title> Delete </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="downloadFolder(props.item)"
                          v-if="props.item.type === 'folder' && admin === true"
                        >
                          <v-list-tile-title> Download </v-list-tile-title>
                        </v-list-tile>
                        <v-list-tile
                          @click="downloadDocument(props.item)"
                          v-if="props.item.type !== 'folder'"
                        >
                          <v-list-tile-title> Download </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </v-layout>
                </td>
              </tr>
            </template>
            <template slot="no-data">
              <v-layout class="text-xs-center grey--text">
                <v-flex>
                  <span
                    class="ml-1"
                    v-if="loading === true || currentCompany === null"
                    >Loading...</span
                  >
                  <span
                    class="ml-1"
                    v-else-if="currentPath === '' && newCompany === true"
                    >Please wait while we're creating default folders</span
                  >
                  <span class="ml-1" v-else>Empty Folder</span>
                </v-flex>
              </v-layout>
            </template>
          </v-data-table>
        </v-layout>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import pdf from 'vue-pdf';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import intersectionBy from 'lodash/intersectionBy';
import PreviewDocument from '@/components/PreviewDocument';
import { getSubdomain } from 'tldjs';
import jwt_decode from 'jwt-decode';
import { supportedExtensionsForPreview } from '@/constants/variables';
import heicConvert from 'heic-convert';

export default {
  name: 'DocumentsPage',
  components: {
    pdf,
    PreviewDocument,
  },
  data() {
    return {
      supportedExtensionsForPreview,
      documentPreviewData: '',
      pdfLoaded: false,
      previewType: '',
      previewDialogOpened: false,
      currentPage: 1,
      currentCompany: null,
      renameFile: false,
      pageCount: 1,
      deleteOldPath: false,
      documentNameRules: [
        v => !!v || 'This field is required.',
        v => !v.includes('.') || 'Document Name cannot have a period',
        v => !v.includes('/') || 'Document Name cannot have a slash',
      ],
      presenceRule: [v => !!v || 'This field is required.'],
      createFolderDialogOpened: false,
      // uploadDocumentDialogOpened: false,
      deleteConfirmationDialogOpened: false,
      deleteMultipleConfirmationDialogOpened: false,
      downloadMultipleConfirmationDialogOpened: false,
      importDocumentsDialogOpened: false,
      renameItemDialogOpened: false,
      selectedItem: {
        name: '',
        type: 'file',
      },
      lastPathInOrder: {
        name: '',
        type: 'file',
      },
      newName: '',
      documentToDelete: {
        name: '',
      },
      newEntityLocation: false,
      documentsToImport: [],
      selectedDocuments: [],
      documentName: '',
      previewRotation: 0,
      documentActualName: '',
      documentExtension: '',
      documentData: '',
      newFolderName: '',
      documentSearch: '',
      documentImporting: false,
      loading: false,
      previewLoaded: false,
      documents: [],
      currentPath: '',
      documentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '60%',
        },
        {
          text: 'Last Modified',
          align: 'left',
          sortable: true,
          value: 'updated_at',
          width: '30%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '15%',
        },
      ],
      mobileDocumentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '95%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ],
      files: [],
      dragAndDropFileDialogOpened: true,
      moveItemDialogOpened: false,
      moveMultipleDocumentsDialogOpened: false,
      pathsInOrder: [],
      itemsInLastPath: [],
      newCompanyId: null,
      admin: false,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      isAdvisor: 'isAdvisor',
      defaultCompany: 'defaultCompany',
    }),
    newCompany() {
      return this.isNewCompany();
    },
    slug() {
      return getSubdomain(window.location.href);
    },
    token() {
      const params = new URLSearchParams(window.location.search);
      return params.get('token');
    },
    paths() {
      const paths = this.currentPath.split('/');
      if (paths) {
        return paths.map((name, index) => {
          return {
            text: name,
            path: paths.slice(0, index + 1).join('/'),
            disabled: paths.length - 1 === index,
          };
        });
      } else {
        return [];
      }
    },
    uploadDisabled() {
      return this.files.length === 0;
    },
    documentUploadReady() {
      return !isEmpty(this.documentActualName) && !isEmpty(this.documentName);
    },
    otherUserCompanies() {
      if (this.defaultCompany.user.is_advisor) {
        return this.companies.filter(c => {
          return (
            c.user.id === this.defaultCompany.user.id &&
            c.id !== this.defaultCompany.id
          );
        });
      } else {
        return this.companies.filter(c => {
          return (
            this.defaultCompany.user.owned_company_ids.includes(c.id) &&
            c.id !== this.defaultCompany.id
          );
        });
      }
    },
  },
  methods: {
    moment,
    get,
    isEmpty,
    heicConvert,
    getParams() {},
    isNewCompany() {
      return (
        this.currentCompany !== null &&
        moment(this.currentCompany.created_at) > moment().subtract(2, 'minute')
      );
    },
    startRenaming(item) {
      if (item) {
        this.selectedItem = item;
        item.renameFile = true;
      } else {
        this.renameFile = true;
      }
      this.documentExtension = this.selectedItem.name.split('.').pop();
      this.newName = this.selectedItem.name.replace(
        `.${this.documentExtension}`,
        '',
      );
    },
    openMoveMultipleDocumentsDialog(deleteOldPath) {
      this.deleteOldPath = deleteOldPath;
      this.newCompanyId = null;
      this.newEntityLocation = false;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.lastPathInOrder = this.currentPath.split('/')[
        this.currentPath.split('/').length - 1
      ];
      this.moveMultipleDocumentsDialogOpened = true;
    },
    openMoveDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = true;
      this.newEntityLocation = false;
      this.newCompanyId = null;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.moveItemDialogOpened = true;
    },
    openCopyDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = false;
      this.newEntityLocation = false;
      this.newCompanyId = null;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.lastPathInOrder = this.currentPath.split('/')[
        this.currentPath.split('/').length - 1
      ];
      this.moveItemDialogOpened = true;
    },
    async goBackPathForMove(path, index) {
      if (index !== this.pathsInOrder.length - 1 && !this.loading) {
        this.loading = true;
        this.lastPathInOrder = null;
        this.pathsInOrder = this.pathsInOrder.slice(0, index + 1);

        const response = await API.integration.getDocuments({
          slug: this.slug,
          token: this.token,
          path: this.pathsInOrder.join('/'),
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.loading = false;
      }
    },
    async changePath() {
      if (this.newCompanyId) {
        const response2 = await API.integration.getDocuments({
          slug: this.slug,
          token: this.token,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      } else {
        const response2 = await API.integration.getDocuments({
          slug: this.slug,
          token: this.token,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      }
    },
    async resetPath() {
      this.itemsInLastPath = [];
      this.pathsInOrder = [''];
      this.lastPathInOrder = null;
      if (this.newCompanyId) {
        const response = await API.integration.getDocuments({
          slug: this.slug,
          token: this.token,
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
      }
    },
    async moveItem() {
      try {
        this.moveItemDialogOpened = false;
        this.loading = true;
        const oldPath = this.currentPath.concat('/', this.selectedItem.name);
        const newPath = this.pathsInOrder
          .join('/')
          .concat('/', this.selectedItem.name);

        const response1 = await API.integration.moveItem({
          slug: this.slug,
          token: this.token,
          oldPath,
          newPath,
          deleteOldPath: this.deleteOldPath,
          isFolder: this.selectedItem.type === 'folder',
        });

        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully ${
            this.deleteOldPath ? 'moved' : 'copied'
          } the ${this.selectedItem.type}!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem ${
            this.deleteOldPath ? 'moving' : 'copying'
          } the ${this.selectedItem.type}.`,
          errorToSend: e,
        });
      } finally {
        this.loading = false;
        this.selectedItem = {
          name: '',
          type: 'file',
        };
        this.pathsInOrder = [];
      }
    },
    async moveMultipleDocuments() {
      try {
        this.loading = true;
        this.moveMultipleDocumentsDialogOpened = false;
        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (doc.name !== '_soraban') {
            const response1 = await API.integration.moveItem({
              slug: this.slug,
              token: this.token,
              oldPath: this.currentPath.concat('/', doc.name),
              newPath: this.pathsInOrder.join('/').concat('/', doc.name),
              deleteOldPath: this.deleteOldPath,
              isFolder: doc.type,
            });
          }
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully ${
            this.deleteOldPath ? 'moved' : 'copied'
          } the documents!`,
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem ${
            this.deleteOldPath ? 'moving' : 'copying'
          } documents.`,
          errorToSend: response.data.error,
        });
      } finally {
        this.loading = false;
      }
    },
    hiddenItem(item) {
      return item.type === 'folder' && item.name === '_soraban';
    },
    async openImportDocumentsDialog() {
      this.documentsToImport = [];
      this.documentImporting = false;
      this.importDocumentsDialogOpened = true;
    },
    async importDocuments() {
      try {
        this.loading = true;
        this.importDocumentsDialogOpened = false;
        const path = this.currentPath;

        let success = 0;

        for (const documentToImport of this.documentsToImport) {
          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            slug: this.slug,
            token: this.token,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            slug: this.slug,
            token: this.token,
            id,
            documentName: documentToImport.name,
            path,
          });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `There was a problem uploading ${documentToImport.name}.`,
            });
          } else {
            success += 1;
          }
        }

        if (this.documentsToImport.length === success) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You have successfully uploaded ${
              this.documentsToImport.length
            } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
          });
        }
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem uploading the document${
            this.documentsToImport.length > 1 ? 's' : ''
          }.`,
          errorToSend: e,
        });
      } finally {
        this.loading = false;
        this.documentsToImport = [];
      }
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },
    async addFile(e) {
      this.importDocumentsDialogOpened = true;
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f != file;
      });
    },
    async onDocumentsPicked(e) {
      this.documentImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentImporting = false;
      }
    },
    openDeleteConfirmationDialog(documentToDelete) {
      this.documentToDelete = documentToDelete;
      this.deleteConfirmationDialogOpened = true;
    },
    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      console.log(response.headers);
      let blob = new Blob([response.data], {
        type: this.get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },
    async renameItem() {
      try {
        this.loading = true;
        const response1 = await API.integration.renameItem({
          slug: this.slug,
          token: this.token,
          itemName: this.selectedItem.name,
          newName:
            this.selectedItem.type === 'folder'
              ? this.newName
              : this.newName.concat('.', this.documentExtension),
          path: this.currentPath,
        });
        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully renamed the ${this.selectedItem.type}!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem renaming the document or folder.',
          errorToSend: e,
        });
      } finally {
        this.loading = false;
        if (!this.previewDialogOpened) {
          this.selectedItem = {
            name: '',
            type: 'file',
          };
        } else {
          this.selectedItem.name = this.newName.concat(
            '.',
            this.documentExtension,
          );
        }
        this.newName = '';
        this.renameFile = false;
        this.documentExtension = '';
      }
    },
    async previewDocument(document) {
      this.previewType = '';
      this.previewLoaded = false;
      this.documentPreviewData = '';
      this.previewRotation = 0;
      this.selectedItem = document;
      this.previewType = this.endsWithAny(
        this.supportedExtensionsForPreview,
        document.link,
      );
      if (this.previewType) {
        if (this.previewType === 'pdf') {
          this.currentPage = 1;
          this.pdfLoaded = false;
        }
        this.previewDialogOpened = true;
      }
      try {
        const response = await API.integration.downloadDocument({
          slug: this.slug,
          token: this.token,
          link: document.link,
        });
        if (response.data.document) {
          if (this.previewType === 'heic') {
            this.documentPreviewData = await this.convertToJPEG(
              response.data.document.data.link,
            );
          } else {
            this.documentPreviewData = response.data.document.data.link;
          }
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
          });
        }
        if (isEmpty(this.previewType))
          this.downloadDocumentByLink(this.documentPreviewData, document.name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
        });
      } finally {
        this.previewLoaded = true;
      }
    },
    async convertToJPEG(url) {
      const output = await fetch(url).then(async data => {
        const buffer = Buffer.from(await data.arrayBuffer());
        return heicConvert({ buffer, format: 'JPEG' });
      });
      const imgBase64 = btoa(
        output.reduce(
          (data, byte) => `${data}${String.fromCharCode(byte)}`,
          '',
        ),
      );
      return `data:image/png;base64,${imgBase64}`;
    },
    openLink(link) {
      window.open(link, '_blank');
    },
    async goToFolder(folderName) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = this.currentPath.concat('/', folderName);
          await this.getDocuments(this.currentPath);
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async goToPath(path) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = path;
          await this.getDocuments(this.currentPath);
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    async downloadDocument(doc) {
      try {
        const response1 = await API.integration.downloadDocument({
          slug: this.slug,
          token: this.token,
          link: doc.link,
        });
        this.downloadDocumentByLink(
          response1.data.document.data.link,
          doc.name,
        );
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },
    async downloadFolder(folder) {
      try {
        await API.integration.downloadFolder({
          slug: this.slug,
          token: this.token,
          link: folder.link,
          name: folder.name,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },
    async downloadMultipleDocuments() {
      try {
        this.loading = true;
        this.downloadMultipleConfirmationDialogOpened = false;
        await this.asyncForEach(
          this.selectedDocuments.filter(doc => doc.type === 'file'),
          async doc => {
            if (doc.name !== '_soraban') {
              const response1 = await API.integration.downloadDocument({
                slug: this.slug,
                token: this.token,
                link: doc.link,
              });

              this.downloadDocumentByLink(
                response1.data.document.data.link,
                doc.name,
              );
            }
          },
        );
        await this.getDocuments(this.currentPath);
        this.loading = false;
        this.selectedDocuments = [];
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading documents.',
        });
      }
    },
    async deleteMultipleDocuments() {
      try {
        this.loading = true;
        this.deleteMultipleConfirmationDialogOpened = false;

        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (doc.name !== '_soraban') {
            const response1 = await API.integration.deleteDocument({
              slug: this.slug,
              token: this.token,
              documentName: doc.name,
              path: this.currentPath,
              isFolder: doc.type === 'folder',
            });
          }
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've deleted the documents",
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem deleting documents.',
          errorToSend: response.data.error,
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteDocument(document) {
      try {
        this.loading = true;
        this.deleteConfirmationDialogOpened = false;

        const response = await API.integration.deleteDocument({
          slug: this.slug,
          token: this.token,
          documentName: document.name,
          path: this.currentPath,
          isFolder: document.type === 'folder',
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async getCurrentCompany() {
      try {
        const response = await API.integration.getCurrentCompany({
          slug: this.slug,
          token: this.token,
        });
        this.currentCompany = response.data.company;
      } catch (error) {
        console.log(error);
      }
    },

    async getPathType(path) {
      try {
        const response = await API.integration.getPathType({
          slug: this.slug,
          token: this.token,
          path,
        });

        if (response.data) {
          return response.data.path_type;
        } else {
          return null;
        }
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    async getDocuments(path) {
      try {
        const response = await API.integration.getDocuments({
          slug: this.slug,
          token: this.token,
          path,
        });

        if (response.data.documents) {
          this.documents = response.data.documents.map(d => {
            const existing = this.documents.find(doc => doc.name === d.name);
            return {
              name: d.name,
              type: d.type,
              link: d.link,
              updated_at: d.updated_at,
              renameFile: existing ? existing.renameFile : false,
            };
          });

          this.selectedDocuments = intersectionBy(
            this.documents,
            this.selectedDocuments,
            'name',
          );
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: "The path doesn't exist",
          });

          this.currentPath = '';
          const response2 = await API.integration.getDocuments({
            slug: this.slug,
            token: this.token,
          });

          this.documents = response2.data.documents.map(d => ({
            name: d.name,
            type: d.type,
            path: d.path,
            renameFile: false,
          }));
          if (this.$route.query.path) {
            this.$router.push('/admin-documents');
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    async createFolder() {
      try {
        this.loading = true;
        this.createFolderDialogOpened = false;

        const path = this.currentPath.concat('/', this.newFolderName);

        const response = await API.integration.createFolder({
          slug: this.slug,
          token: this.token,
          path,
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.newFolderName = '';
        this.loading = false;
      }
    },
  },
  async created() {
    if (this.token) {
      const decoded = jwt_decode(this.token);

      this.admin = decoded.admin;

      if (decoded.css) {
        const style = document.createElement('style');
        document.head.appendChild(style);

        forEach(decoded.css, (value, key) => {
          const styles = value
            .map(style => {
              return (
                (style.endsWith(';') ? style.slice(0, -1) : style) +
                '!important'
              );
            })
            .join(';');

          const css = `${key} { ${styles} }`;

          style.sheet.insertRule(css);
        });
      }

      await this.getCurrentCompany();

      if (this.isNewCompany()) {
        const stopPulling = setInterval(() => {
          if (this.isNewCompany()) {
            console.log('Pulling...');
            this.getDocuments(this.currentPath);
          } else {
            console.log('Stopped pulling.');
            clearInterval(stopPulling);
          }
        }, 3000);
      }
    }

    this.loading = true;

    try {
      const path =
        this.$route.query.path === 'all' || !this.$route.query.path
          ? ''
          : this.$route.query.path;

      const path_type = await this.getPathType(path);

      if (path_type === 'folder') {
        this.currentPath = path;
        await this.getDocuments(this.currentPath);
      } else if (path_type === 'file') {
        this.currentPath = path.substring(0, path.lastIndexOf('/'));

        await this.getDocuments(this.currentPath);

        const fileName = path.substring(path.lastIndexOf('/') + 1);

        const document = this.documents.find(doc => doc.name === fileName);
        if (document !== null) {
          this.previewDocument(document);
        }
      } else {
        this.currentPath = '';
        await this.getDocuments(this.currentPath);
      }

      if (this.$route.query.path) {
        this.$router.replace({
          ...this.$router.currentRoute,
          query: {
            ...this.$router.currentRoute.query,
            path: undefined,
          },
        });
      }
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="postcss" scoped>
.documents-title {
  font-size: 34px;
}

.dialog-title {
  font-size: 22px;
  font-weight: 700;
}

.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #eee;
  border: 1px solid rgba(0, 0, 0, 0.15);

  & >>> i {
    color: rgba(0, 0, 0, 0.3);
  }

  & >>> span {
    color: rgba(0, 0, 0, 0.6);
  }

  & >>> span.clickable {
    cursor: pointer;
  }
}

.search-input {
  font-size: 14px;

  & >>> .v-input__slot {
    border: 1px solid #bdbdbd !important;
  }
}

.breadcrumbs {
  & >>> .clickable {
    cursor: pointer;
    color: #777;
  }
  & >>> .clickable:hover {
    color: #000;
  }
}

.documents-table {
  & >>> table.v-table thead th {
    font-weight: 700;
    font-size: 14px;
    color: #666;
  }
  & >>> table.v-table tbody td {
    font-size: 14px;
  }
  & >>> table.v-table .v-input__slot .theme--light {
    color: #ccc;
  }
  & >>> .file-icon {
    color: #545454;
  }
}

.new-folder-btn {
  text-transform: none;
  font-weight: 700;
  padding: 12px;
}

.action-btn {
  text-transform: none;
  font-weight: 700;
  padding: 12px;
}

.new-folder-btn i,
.action-btn i {
  margin-right: 4px;
}

.v-list {
  & >>> .v-list__tile {
    font-size: 14px;
  }
}

.documents {
  &__icon {
    font-size: 24px;
  }
}

.document-action {
  &__icon {
    font-size: 20px;
  }
}

.documents-row {
  &__updated-date {
    color: #333;
  }
}

.image-holder {
  display: block;
  width: 200px;
  height: 200px;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}
</style>
